import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Force language change based on route
  const isFrench = location.pathname.startsWith('/fr');
  if (isFrench && i18n.language !== 'fr') {
    i18n.changeLanguage('fr');
  } else if (!isFrench && i18n.language !== 'en') {
    i18n.changeLanguage('en');
  }

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/fr/, '');
    return pathWithoutLang === path || currentPath === path;
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link 
      to={to}
      className={cn(
        "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
        isActive(to)
          ? "text-primary bg-primary/10" 
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )}
      onClick={() => setIsMenuOpen(false)}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center"
          >
            <span className="text-xl font-bold tracking-tight text-foreground">
              NoQ5<span className="text-primary">.ai</span>
            </span>
          </motion.div>
        </Link>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavLink to={isFrench ? '/fr' : '/'}>{t('nav.home')}</NavLink>
          <NavLink to={isFrench ? '/fr/a-propos' : '/about'}>{t('footer.aboutUs')}</NavLink>
          <NavLink to={isFrench ? '/fr/blog' : '/blog'}>{t('nav.blog')}</NavLink>
          <NavLink to={isFrench ? '/fr/tarifs' : '/pricing'}>{t('nav.pricing')}</NavLink>
          <NavLink to={isFrench ? '/fr/comparison' : '/comparison'}>{t('nav.compare')}</NavLink>
        </nav>
        
        {/* Right side */}
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          
          <Button 
            variant="ghost" 
            className="hidden sm:inline-flex text-muted-foreground hover:text-foreground"
            asChild
          >
            <a href="https://app.noq5.ai/" target="_blank" rel="noopener noreferrer">
              {t('nav.login')}
            </a>
          </Button>
          
          <Button 
            className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-button hover:shadow-glow transition-all duration-300 group"
            asChild
          >
            <a href="https://app.noq5.ai/trial" target="_blank" rel="noopener noreferrer">
              {t('nav.tryFree')}
              <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </Button>
          
          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      
      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="md:hidden absolute top-16 left-0 right-0 bg-background/98 backdrop-blur-xl border-b border-border shadow-lg"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <nav className="container py-4 flex flex-col gap-1">
              <NavLink to={isFrench ? '/fr' : '/'}>{t('nav.home')}</NavLink>
              <NavLink to={isFrench ? '/fr/a-propos' : '/about'}>{t('footer.aboutUs')}</NavLink>
              <NavLink to={isFrench ? '/fr/blog' : '/blog'}>{t('nav.blog')}</NavLink>
              <NavLink to={isFrench ? '/fr/tarifs' : '/pricing'}>{t('nav.pricing')}</NavLink>
              <NavLink to={isFrench ? '/fr/comparison' : '/comparison'}>{t('nav.compare')}</NavLink>
              
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border">
                <a 
                  href="https://app.noq5.ai/"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.login')}
                </a>
                <a 
                  href="https://app.noq5.ai/trial"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-3 bg-primary text-primary-foreground text-center font-medium rounded-lg shadow-button"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.tryFree')}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
